// useWorkflowStepNodes.js

import { useEffect, useMemo, useRef, useState } from 'react';

import { subscribeToNodesByKeys } from 'services/nodes/NodeReadService';
import { updateNode } from 'services/nodes/NodeWriteService';
import { normalizeMissionPeople } from 'missions/taskmodal/missionTaskModalUtils';
import { collectWorkflowStepNodeKeys } from 'node/missions/mission/utils/workflowStepNodeUtils';

const EMPTY_MAP = new Map();

/**
 * Heal early N1 mints that wrote `data_task.assignee` instead of `assignees`.
 * The TASK form field is `assignees` (plural); legacy singular must be migrated.
 *
 * @param {object|null} stepNode
 * @param {object|null} runTask
 * @returns {Promise<void>}
 */
async function healStepNodeAssigneesField(stepNode, runTask = null) {
  const nodeKey = String(stepNode?.key || '').trim();
  if (!nodeKey || !stepNode) return;

  const dataTask = stepNode.data_task && typeof stepNode.data_task === 'object'
    ? stepNode.data_task
    : {};
  const canonicalPeople = normalizeMissionPeople(dataTask.assignees);
  if (canonicalPeople.length > 0) return;

  const legacyPeople = normalizeMissionPeople(dataTask.assignee);
  const runPeople = normalizeMissionPeople(runTask?.assignees);
  const people = legacyPeople.length > 0 ? legacyPeople : runPeople;
  if (!people.length) return;

  const nextDataTask = {
    ...dataTask,
    assignees: people,
  };
  if (Object.prototype.hasOwnProperty.call(nextDataTask, 'assignee')) {
    delete nextDataTask.assignee;
  }

  await updateNode(nodeKey, {
    data_task: nextDataTask,
  });
}

/**
 * useWorkflowStepNodes
 *
 * Live subscription to TASK nodes linked from a workflow run's people steps.
 *
 * @param {object|null} missionRun
 * @returns {{ stepNodesByKey: Map<string, object>, stepNodeKeys: string[] }}
 */
export default function useWorkflowStepNodes(missionRun = null) {
  const stepNodeKeys = useMemo(
    () => collectWorkflowStepNodeKeys(missionRun),
    [missionRun]
  );

  const stepNodeKeysSignature = stepNodeKeys.join('\u0001');
  const [stepNodesByKey, setStepNodesByKey] = useState(EMPTY_MAP);
  const healedKeysRef = useRef(new Set());

  useEffect(() => {
    const keys = stepNodeKeysSignature
      ? stepNodeKeysSignature.split('\u0001').filter(Boolean)
      : [];

    healedKeysRef.current = new Set();

    if (!keys.length) {
      setStepNodesByKey(EMPTY_MAP);
      return undefined;
    }

    return subscribeToNodesByKeys(keys, (rows) => {
      const next = new Map();
      (Array.isArray(rows) ? rows : []).forEach((node) => {
        const key = String(node?.key || '').trim();
        if (key) next.set(key, node);
      });
      setStepNodesByKey(next);
    });
  }, [stepNodeKeysSignature]);

  useEffect(() => {
    if (!(stepNodesByKey instanceof Map) || stepNodesByKey.size === 0) return;

    const runTasks = Array.isArray(missionRun?.tasks) ? missionRun.tasks : [];
    const runTaskByNodeKey = new Map(
      runTasks
        .map((task) => [String(task?.nodeKey || '').trim(), task])
        .filter(([key]) => Boolean(key))
    );

    stepNodesByKey.forEach((stepNode, nodeKey) => {
      if (healedKeysRef.current.has(nodeKey)) return;
      healedKeysRef.current.add(nodeKey);

      healStepNodeAssigneesField(stepNode, runTaskByNodeKey.get(nodeKey) || null)
        .catch((error) => {
          healedKeysRef.current.delete(nodeKey);
          console.warn('[useWorkflowStepNodes] assignee heal failed:', nodeKey, error);
        });
    });
  }, [missionRun?.tasks, stepNodesByKey]);

  return {
    stepNodeKeys,
    stepNodesByKey,
  };
}
